import { Component, h, Host, Prop } from '@stencil/core'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/menu?tab=designer).
 */
@Component({
  tag: 'br-menu-item',
  styleUrl: 'menu-item.scss',
  shadow: true,
})
export class MenuItem {
  /**
   * Ícone exibido no item do menu.
   * Utilize esta propriedade para definir o ícone que será mostrado ao lado do conteúdo do item.
   */
  @Prop() icon: string

  /**
   * URL de destino do link do item do menu.
   * Utilize esta propriedade para definir o endereço para onde o usuário será direcionado ao clicar no item.
   */
  @Prop() href: string = '#'

  /**
   * Define como o link será aberto.
   * - `_self`: abre no mesmo contexto de navegação (padrão)
   * - `_blank`: abre em nova aba ou janela
   * - `_parent`: abre no contexto pai
   * - `_top`: abre no contexto de nível superior
   */
  @Prop() target?: '_self' | '_blank' | '_parent' | '_top' = '_blank'

  /**
   * Define se o item deve exibir um divisor visual.
   * Quando ativado, adiciona uma linha separadora para organizar visualmente os itens do menu.
   */
  @Prop() divider: boolean = false

  /**
   * Define se o item do menu está ativo/selecionado.
   * Quando ativado, aplica estilos visuais para destacar o item como atual ou selecionado.
   */
  @Prop({ mutable: true, reflect: true }) active: boolean = false

  /**
   * Define o modo de exibição do item, controlando os ícones de expansão.
   * - `accordion`: exibe setas para cima/baixo (usado em menus de nível 0).
   * - `drill-down`: exibe seta para a direita (usado em menus de níveis 1+).
   * - `none`: não exibe ícones de expansão.
   */
  @Prop() displayMode: 'accordion' | 'drill-down' | 'none' = 'none'

  /**
   * Indica se o item está expandido para fins de acessibilidade (ARIA).
   * Este estado é controlado externamente (por exemplo, por br-menu-list).
   * Não altera o estado visual de "active".
   */
  @Prop() expanded: boolean = false

  render() {
    const classes = {
      'menu-item': true,
      'divider-item': this.divider,
      active: this.active,
    }

    const isExpandable = this.displayMode !== 'none'
    const aria = isExpandable ? { 'aria-expanded': String(this.expanded), 'aria-haspopup': 'true' } : {}
    const targetAttr = !isExpandable && this.target ? { target: this.target } : {}

    const anchor = (
      <a href={this.href} class={classes} role="treeitem" {...aria} {...targetAttr} aria-selected={String(this.active)}>
        {this.icon && (
          <span class="icon">
            <br-icon icon-name={`fa6-solid:${this.icon}`} aria-hidden="true"></br-icon>
          </span>
        )}
        <span class="content">
          <slot></slot>
        </span>
        {this.displayMode === 'accordion' && (
          <span class="support">
            <br-icon
              icon-name={`fa6-solid:${this.active ? 'chevron-up' : 'chevron-down'}`}
              aria-hidden="true"
            ></br-icon>
          </span>
        )}
        {this.displayMode === 'drill-down' && (
          <span class="support">
            <br-icon icon-name={`fa6-solid:angle-right`} aria-hidden="true"></br-icon>
          </span>
        )}
      </a>
    )

    return <Host>{anchor}</Host>
  }
}
